// A value type compared by equality: the server's value rule plus how two values match.
export interface EqualityValueKind<Value> {
  readonly equals: (actual: Value, expected: Value) => boolean;
  readonly isValue: (value: unknown) => value is Value;
}

// A value type with an order: the server's value rule plus a three-way compare.
export interface OrderedValueKind<Value> {
  readonly compare: (actual: Value, expected: Value) => number;
  readonly isValue: (value: unknown) => value is Value;
}

const VERSION_PATTERN = /^\d+(\.\d+){0,2}$/;
const CODE_PUSH_LABEL_PATTERN = /^v\d+$/;

const isSameValue = <Value>(actual: Value, expected: Value): boolean => actual === expected;

export const STRING_KIND: EqualityValueKind<string> = {
  equals: isSameValue,
  isValue: (value): value is string => typeof value === "string" && value.length > 0,
};

export const INTEGER_KIND: EqualityValueKind<number> = {
  equals: isSameValue,
  isValue: (value): value is number => typeof value === "number" && Number.isInteger(value),
};

const compareVersions = (actual: string, expected: string): number => {
  const actualSegments = actual.split(".").map(Number);
  const expectedSegments = expected.split(".").map(Number);
  const segmentCount = Math.max(actualSegments.length, expectedSegments.length);
  for (let index = 0; index < segmentCount; index += 1) {
    const difference = (actualSegments[index] ?? 0) - (expectedSegments[index] ?? 0);
    if (difference !== 0) {
      return difference;
    }
  }
  return 0;
};

export const VERSION_KIND: OrderedValueKind<string> = {
  compare: compareVersions,
  isValue: (value): value is string => typeof value === "string" && VERSION_PATTERN.test(value),
};

const toCodePushNumber = (label: string): number => Number(label.slice(1));

export const CODE_PUSH_LABEL_KIND: OrderedValueKind<string> = {
  compare: (actual, expected) => toCodePushNumber(actual) - toCodePushNumber(expected),
  isValue: (value): value is string =>
    typeof value === "string" && CODE_PUSH_LABEL_PATTERN.test(value),
};
